// Wallet Ops → Blacklist — the crypto address denylist (SM-211).
// Blacklisted addresses are refused as withdrawal destinations.
// Add / remove gate on CRYPTO_WALLET_MANUAL_OP (server-enforced);
// removal captures an operator reason via the ConfirmModal, logged in
// the backoffice audit trail.
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Pagination } from "@/components/ui/Pagination";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";
import { usePagination } from "@/lib/usePagination";
import { useNetworkMap, networkLabel } from "@/components/wallet-ops/orders";
import { useAdminMap, adminName } from "@/hooks/useAdminNames";
import {
  addCryptoBlacklist,
  fetchCryptoBlacklist,
  removeCryptoBlacklist,
  walletKeys,
} from "@/lib/wallet-queries";
import type { CryptoBlacklistEntry } from "@/lib/wallet-types";
import { fmtRelative } from "@/lib/format";

export const Route = createFileRoute("/_app/wallet-ops/blacklist")({
  component: BlacklistPage,
});

const th =
  "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";

function BlacklistPage() {
  const qc = useQueryClient();
  const nets = useNetworkMap();
  const admins = useAdminMap();
  const perms = useMyPerms();
  const canManage =
    !perms.loaded || perms.isSuperAdmin || perms.has(Perm.CryptoWalletManualOp);

  const [networkFilter, setNetworkFilter] = useState("");
  const { page, setPage, limit, offset } = usePagination();
  useEffect(() => {
    setPage(0);
  }, [networkFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = useQuery({
    queryKey: walletKeys.blacklist(networkFilter.trim() || undefined, limit, offset),
    queryFn: () =>
      fetchCryptoBlacklist({
        network_id: networkFilter.trim() || undefined,
        limit,
        offset,
      }),
  });

  // --- add form ---
  const [addOpen, setAddOpen] = useState(false);
  const [networkID, setNetworkID] = useState("");
  const [address, setAddress] = useState("");
  const [reason, setReason] = useState("");
  const add = useMutation({
    mutationFn: () =>
      addCryptoBlacklist({
        network_id: networkID.trim(),
        address: address.trim(),
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      setAddOpen(false);
      setNetworkID("");
      setAddress("");
      setReason("");
      await qc.invalidateQueries({ queryKey: walletKeys.all });
    },
    meta: { successMessage: "Address blacklisted." },
  });

  // --- remove (confirm with reason) ---
  const [removeTarget, setRemoveTarget] = useState<CryptoBlacklistEntry | null>(null);
  const remove = useMutation({
    mutationFn: ({ entry, why }: { entry: CryptoBlacklistEntry; why: string }) =>
      removeCryptoBlacklist(entry.network_id, entry.address, why),
    onSuccess: async () => {
      setRemoveTarget(null);
      await qc.invalidateQueries({ queryKey: walletKeys.all });
    },
    meta: { successMessage: "Address removed from the blacklist." },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Blacklist</h1>
        <p className="text-sm text-muted">
          Blacklisted destination addresses. Withdrawals to a listed address
          are rejected at validation.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Input
            placeholder="Filter by network…"
            value={networkFilter}
            onChange={(e) => setNetworkFilter(e.target.value)}
          />
        </div>
        <div className="flex-1" />
        {canManage && (
          <Button onClick={() => setAddOpen((s) => !s)}>
            {addOpen ? "Cancel" : "Blacklist an address"}
          </Button>
        )}
      </div>

      {addOpen && canManage && (
        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle>Blacklist an address</CardTitle>
          </CardHeader>
          <CardBody>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                add.mutate();
              }}
            >
              <div className="grid gap-4 sm:grid-cols-3">
                <Select
                  label="Network"
                  value={networkID}
                  onChange={(e) => setNetworkID(e.target.value)}
                  placeholder="Pick a network"
                  options={[...nets.values()]
                    .filter((n) => !n.archived)
                    .map((n) => ({ value: n.network_id, label: n.name }))}
                />
                <Input
                  label="Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="0x…"
                  className="font-mono"
                  required
                />
                <Input
                  label="Reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="sanctions hit, fraud report, …"
                  required
                />
              </div>
              {add.isError && (
                <p className="text-2xs text-no">{(add.error as Error).message}</p>
              )}
              <Button
                type="submit"
                variant="danger"
                loading={add.isPending}
                disabled={!networkID || !address.trim() || !reason.trim() || add.isPending}
              >
                Blacklist address
              </Button>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Blacklist</CardTitle>
          <span className="text-2xs text-muted">{list.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {list.isLoading && (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {list.isError && (
            <p className="px-5 py-16 text-center text-sm text-no">
              {(list.error as Error).message}
            </p>
          )}
          {list.data && list.data.items.length === 0 && (
            <p className="px-5 py-16 text-center text-sm text-muted">
              {networkFilter
                ? "No entries match the current filter."
                : "No blacklisted addresses."}
            </p>
          )}
          {list.data && list.data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Address</th>
                    <th className={th}>Network</th>
                    <th className={th}>Reason</th>
                    <th className={th}>Added by</th>
                    <th className={th}>Added</th>
                    {canManage && (
                      <th className="px-5 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted" />
                    )}
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((e) => (
                    <tr
                      key={`${e.network_id}:${e.address}`}
                      className="border-b border-border/60 last:border-0"
                    >
                      <td className={`${td} px-5 font-mono text-2xs`}>{e.address}</td>
                      <td className={td} title={e.network_id}>{networkLabel(nets, e.network_id)}</td>
                      <td className={`${td} max-w-[280px] truncate`} title={e.reason}>
                        {e.reason || "—"}
                      </td>
                      <td className={td} title={e.created_by}>{adminName(admins, e.created_by)}</td>
                      <td className={`${td} text-2xs text-muted`}>
                        {fmtRelative(e.created_at)}
                      </td>
                      {canManage && (
                        <td className={`${td} px-5 text-right`}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              remove.reset();
                              setRemoveTarget(e);
                            }}
                          >
                            Remove
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {list.data && list.data.page.total > 0 && (
            <Pagination
              page={list.data.page}
              currentPage={page}
              onPageChange={setPage}
              itemLabel="entries"
            />
          )}
        </CardBody>
      </Card>

      <ConfirmModal
        open={removeTarget !== null}
        tone="danger"
        title="Remove this address from the blacklist?"
        description={
          removeTarget && (
            <p>
              <span className="font-mono text-2xs">{removeTarget.address}</span>{" "}
              on <strong>{networkLabel(nets, removeTarget.network_id)}</strong> becomes a valid
              withdrawal destination again immediately.
            </p>
          )
        }
        confirmLabel="Remove"
        cancelLabel="Keep it"
        reasonLabel="Reason (audit log)"
        reasonRequired
        loading={remove.isPending}
        error={remove.isError ? (remove.error as Error).message : null}
        onConfirm={(why) =>
          removeTarget && remove.mutate({ entry: removeTarget, why })
        }
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}
